import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { canonicalMoney, multiplyByPercent, subtractMoney, formatMoney, compareMoney } from '../../lib/money';
import type { KioskService, KioskOperator, DynamicPriceResult } from './hooks';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function formatDate(date: string): string {
  return new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function formatTime(t: string): string {
  const [hh, mm] = t.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  return `${hh % 12 || 12}:${mm.toString().padStart(2, '0')} ${ampm}`;
}

interface PromoResult { valid: boolean; promo_code_id?: string; discount_type?: string; discount_value?: number; max_discount_amount?: number; description?: string; error?: string; }

interface Props {
  service: KioskService;
  operator: KioskOperator | null;
  date: string;
  time: string;
  clientName: string;
  clientPhone: string;
  accentColor: string;
  headingFont: string;
  cancellationPolicy: string | null;
  submitting: boolean;
  error: string | null;
  dynamicPrice?: DynamicPriceResult | null;
  businessId?: string;
  onPromoApplied?: (promoCodeId: string, discountAmount: number, desc: string) => void;
  onConfirm: () => void;
  onBack: () => void;
  onRetryTime: () => void;
}

export function KioskConfirmation({ service, operator, date, time, clientName, clientPhone, accentColor, headingFont, cancellationPolicy, submitting, error, dynamicPrice, businessId, onPromoApplied, onConfirm, onBack, onRetryTime }: Props) {
  const [promoCode, setPromoCode] = useState('');
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [discountAmt, setDiscountAmt] = useState(0);

  const basePrice = dynamicPrice?.is_dynamic ? dynamicPrice.price : Number(service.price);

  const handleApplyPromo = async () => {
    if (!promoCode.trim() || !businessId) return;
    setPromoLoading(true); setPromoError(''); setPromoSuccess('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('validate_promo_code', {
        p_business_id: businessId, p_code: promoCode.trim(),
        p_client_phone: clientPhone, p_service_ids: [service.id], p_order_total: basePrice,
      });
      if (rpcErr) throw rpcErr;
      const result = data as PromoResult;
      if (!result.valid) { setPromoError(result.error ?? 'Invalid code'); setPromoLoading(false); return; }
      // Rule #34: canonical money math for discount calculation.
      const basePriceCanonical = canonicalMoney(basePrice);
      let discountCanonical = result.discount_type === 'percentage'
        ? multiplyByPercent(basePriceCanonical, result.discount_value!)
        : canonicalMoney(result.discount_value!);
      if (result.max_discount_amount && compareMoney(discountCanonical, canonicalMoney(result.max_discount_amount)) > 0) {
        discountCanonical = canonicalMoney(result.max_discount_amount);
      }
      const discount = Number(discountCanonical);
      setDiscountAmt(discount); setPromoSuccess(`-${formatMoney(discountCanonical)} off${result.description ? ` (${result.description})` : ''}`);
      onPromoApplied?.(result.promo_code_id!, discount, promoCode.toUpperCase());
    } catch { setPromoError('Could not validate code.'); }
    finally { setPromoLoading(false); }
  };

  // Display price: after-discount money formatted with cents when discount applied,
  // otherwise whole-dollar format per the kiosk's marketing treatment for undiscounted prices.
  const displayPrice = discountAmt > 0
    ? subtractMoney(canonicalMoney(basePrice), canonicalMoney(discountAmt))
    : basePrice.toFixed(0);

  return (
    <div style={{ padding: '0 24px' }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A', fontFamily: headingFont, margin: '28px 0 16px' }}>
        Confirm your booking
      </h2>

      {/* Summary card */}
      <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #E5E5EA' }}>
        <div style={{ height: 4, background: accentColor }} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Row label="Service" value={service.name} sub={`${service.duration_minutes} min · $${displayPrice}${discountAmt > 0 ? ` (was $${basePrice.toFixed(0)})` : ''}`} />
          <Row label="With" value={operator?.name ?? 'First available'} />
          <Row label="When" value={`${formatDate(date)} at ${formatTime(time)}`} />
          <div style={{ borderTop: '1px solid #F1F3F5', paddingTop: 14 }}>
            <Row label="Name" value={clientName} />
            <Row label="Phone" value={clientPhone} />
          </div>
        </div>
      </div>

      {/* Promo code */}
      {businessId && (
        <div style={{ marginTop: 14 }}>
          {!promoOpen && !promoSuccess ? (
            <button onClick={() => setPromoOpen(true)} style={{ background: 'none', border: 'none', color: accentColor, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: BODY_FONT, padding: 0 }}>Have a promo code?</button>
          ) : promoSuccess ? (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: 14, color: '#15803D', fontFamily: BODY_FONT }}>{promoSuccess}</div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="Enter code" style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E5EA', fontSize: 14, fontFamily: BODY_FONT, outline: 'none', textTransform: 'uppercase' }} />
              <button onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: accentColor, color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: BODY_FONT, cursor: 'pointer', opacity: promoLoading ? 0.7 : 1 }}>{promoLoading ? '...' : 'Apply'}</button>
            </div>
          )}
          {promoError && <div style={{ fontSize: 13, color: '#D13438', marginTop: 6, fontFamily: BODY_FONT }}>{promoError}</div>}
        </div>
      )}

      {cancellationPolicy && (
        <div style={{ fontSize: 12, color: '#ADB5BD', fontFamily: BODY_FONT, marginTop: 12, lineHeight: 1.5 }}>
          {cancellationPolicy}
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 16, padding: '12px 16px', borderRadius: 12,
          background: '#FEF2F2', border: '1px solid #FECACA',
          fontFamily: BODY_FONT, fontSize: 14, color: '#D13438',
        }}>
          {error}
          {error.toLowerCase().includes('no longer available') && (
            <button onClick={onRetryTime} style={{
              display: 'block', marginTop: 8, background: 'none', border: 'none',
              color: '#D13438', fontWeight: 600, cursor: 'pointer', fontFamily: BODY_FONT, fontSize: 14, padding: 0,
            }}>
              Pick another time &rarr;
            </button>
          )}
        </div>
      )}

      <button onClick={onConfirm} disabled={submitting} style={{
        width: '100%', marginTop: 20, padding: '16px 0', borderRadius: 14, border: 'none',
        background: accentColor, color: '#fff', fontSize: 16, fontWeight: 600,
        fontFamily: BODY_FONT, cursor: submitting ? 'default' : 'pointer',
        opacity: submitting ? 0.7 : 1, transition: 'opacity 200ms ease',
        height: 52,
      }}>
        {submitting ? 'Booking...' : 'Confirm Booking'}
      </button>

      <div style={{ textAlign: 'center', marginTop: 14, paddingBottom: 24 }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: '#636366', fontSize: 14,
          fontFamily: BODY_FONT, cursor: 'pointer', padding: 4,
        }}>
          Go back and edit
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontFamily: BODY_FONT }}>
      <span style={{ fontSize: 14, color: '#636366' }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#1A1A1A' }}>{value}</span>
        {sub && <div style={{ fontSize: 12, color: '#ADB5BD', marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}
